import { parse as parseWithSourceMap } from 'json-source-map';

export function parseJson(text) {
  try {
    return { ok: true, value: JSON.parse(text) };
  } catch (error) {
    return { ok: false, error: normalizeJsonError(error, text) };
  }
}

export function formatJson(text, spaces = 2) {
  const parsed = parseJson(text);
  if (!parsed.ok) return parsed;
  return { ok: true, value: JSON.stringify(parsed.value, null, spaces) };
}

export function minifyJson(text) {
  const parsed = parseJson(text);
  if (!parsed.ok) return parsed;
  return { ok: true, value: JSON.stringify(parsed.value) };
}

export function getJsonChildRangesAtPosition(text, position) {
  const parsed = parseJson(text);
  if (!parsed.ok) return parsed;

  const sourceMap = parseWithSourceMap(text);
  const targetPointer = findPointerAtPosition(sourceMap.pointers, text, position);
  const siblingParent = getParentPointer(targetPointer);
  const levelPointers = targetPointer === ''
    ? ['']
    : Object.keys(sourceMap.pointers).filter((pointer) => pointer && getParentPointer(pointer) === siblingParent);
  const ranges = Object.keys(sourceMap.pointers)
    .filter((pointer) => levelPointers.includes(getParentPointer(pointer)))
    .map((pointer) => ({
      from: sourceMap.pointers[pointer].value.pos,
      to: sourceMap.pointers[pointer].valueEnd.pos,
    }))
    .filter((range) => range.from < range.to)
    .sort((left, right) => left.from - right.from);

  return { ok: true, ranges };
}

export function escapeJsonText(text) {
  return JSON.stringify(text);
}

export function unescapeJsonText(text) {
  const parsed = parseJson(text);
  if (!parsed.ok) return parsed;
  if (typeof parsed.value !== 'string') {
    return {
      ok: false,
      error: { message: '反转义内容必须是带外层引号的 JSON 字符串。' },
    };
  }
  return { ok: true, value: parsed.value };
}

export function stringifyDisplay(value) {
  if (value === undefined) return '—';
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

function normalizeJsonError(error, text) {
  const message = error instanceof Error ? error.message : String(error);
  const positionMatch = message.match(/position\s+(\d+)/i);
  let position;
  let line;
  let column;

  if (positionMatch) {
    position = Number(positionMatch[1]);
    const before = text.slice(0, position);
    const rows = before.split('\n');
    line = rows.length;
    column = rows.at(-1).length + 1;
  } else {
    const lineColumnMatch = message.match(/line\s+(\d+)\s+column\s+(\d+)/i);
    if (lineColumnMatch) {
      line = Number(lineColumnMatch[1]);
      column = Number(lineColumnMatch[2]);
    }
  }

  return { message, position, line, column };
}

function findPointerAtPosition(pointers, text, position) {
  const safePosition = Math.min(Math.max(position, 0), text.length);
  const lineStart = text.lastIndexOf('\n', safePosition - 1) + 1;
  const lineEndIndex = text.indexOf('\n', safePosition);
  const lineEnd = lineEndIndex === -1 ? text.length : lineEndIndex;
  const entries = Object.entries(pointers);
  const depth = ([pointer]) => pointer === '' ? 0 : pointer.split('/').length - 1;
  const deepest = (matches) => matches.sort((left, right) => depth(right) - depth(left))[0]?.[0];

  const onProperty = deepest(entries.filter(([, location]) => (
    location.key && location.key.pos <= safePosition && safePosition <= location.valueEnd.pos
  )));
  if (onProperty !== undefined) return onProperty;

  const onPropertyLine = deepest(entries.filter(([, location]) => (
    location.key && location.key.pos >= lineStart && location.key.pos <= lineEnd
  )));
  if (onPropertyLine !== undefined) return onPropertyLine;

  return deepest(entries.filter(([, location]) => (
    location.value.pos <= safePosition && safePosition <= location.valueEnd.pos
  ))) ?? '';
}

function getParentPointer(pointer) {
  if (pointer === '') return '';
  return pointer.slice(0, pointer.lastIndexOf('/'));
}
